import { IndustryType } from '../../shared/models/industry-type.model';

/**
 * Catalog UX profile per industry. Default option axes are suggestions only —
 * each product can define its own axes at runtime.
 */
export interface IndustryCatalogProfile {
  industry: IndustryType;
  supportsVariants: boolean;
  /** Default axes pre-filled when creating a new product with variants */
  defaultOptionAxes: string[];
  /** Chip suggestions when adding/editing option attributes */
  optionAxisSuggestions: string[];
  catalogBannerKey: string;
  productNounKey: string;
}

const FALLBACK_PROFILE: Omit<IndustryCatalogProfile, 'industry'> = {
  supportsVariants: false,
  defaultOptionAxes: [],
  optionAxisSuggestions: [],
  catalogBannerKey: 'inventoryPage.banner.title',
  productNounKey: 'inventoryPage.nav.inventory',
};

const PROFILES: Partial<Record<IndustryType, Omit<IndustryCatalogProfile, 'industry'>>> = {
  [IndustryType.RetailAndEcommerce]: {
    supportsVariants: true,
    defaultOptionAxes: ['Color', 'Size'],
    optionAxisSuggestions: [
      'Color',
      'Size',
      'Material',
      'Finish',
      'Pack',
      'Style',
      'Capacity',
      'Voltage',
      'Storage',
      'Length',
      'Width',
    ],
    catalogBannerKey: 'inventoryPage.retail.banner.title',
    productNounKey: 'inventoryPage.retail.nav.inventory',
  },
  [IndustryType.FoodAndBeverage]: {
    supportsVariants: true,
    defaultOptionAxes: ['Portion', 'Spice level'],
    optionAxisSuggestions: [
      'Portion',
      'Spice level',
      'Size',
      'Crust',
      'Add-on',
      'Temperature',
      'Milk type',
    ],
    catalogBannerKey: 'inventoryPage.banner.title',
    productNounKey: 'inventoryPage.nav.inventory',
  },
  [IndustryType.HealthcareAndPharma]: {
    supportsVariants: true,
    defaultOptionAxes: ['Strength', 'Pack size'],
    optionAxisSuggestions: ['Strength', 'Pack size', 'Form', 'Volume'],
    catalogBannerKey: 'inventoryPage.banner.title',
    productNounKey: 'inventoryPage.nav.inventory',
  },
  [IndustryType.Barbershop]: {
    supportsVariants: true,
    defaultOptionAxes: ['Duration', 'Style'],
    optionAxisSuggestions: ['Duration', 'Style', 'Add-on'],
    catalogBannerKey: 'inventoryPage.banner.title',
    productNounKey: 'inventoryPage.nav.inventory',
  },
  [IndustryType.Salon]: {
    supportsVariants: true,
    defaultOptionAxes: ['Duration', 'Service type'],
    optionAxisSuggestions: ['Duration', 'Service type', 'Add-on', 'Length'],
    catalogBannerKey: 'inventoryPage.banner.title',
    productNounKey: 'inventoryPage.nav.inventory',
  },
};

export function getIndustryCatalogProfile(
  industry: IndustryType | null | undefined,
): IndustryCatalogProfile {
  const type = industry ?? null;
  if (type == null) {
    return {
      industry: IndustryType.RetailAndEcommerce,
      ...FALLBACK_PROFILE,
    };
  }
  const profile = PROFILES[type] ?? FALLBACK_PROFILE;
  return {
    industry: type,
    ...profile,
  };
}
